// Swallowed: the first attachment kind that does something.
//
// docs/TOKEN-ATTACHMENTS.md §5 makes the case that a mechanical attachment kind
// is an EffectDef — conditions while it lasts, dice at the start of somebody's
// turn, ends on a trigger — and swallowed is the one that proves it: 29 SRD
// monsters use it, and the wording barely varies.
//
// This module reads the numbers out of the swallower's own statblock rather than
// keeping a table of monsters. The same argument as the spell target cap: a hand
// table is more rules data to get wrong, and the text is already on hand.
//
// The SRD shape, from the behir, verbatim:
//
//   "While swallowed, the target is blinded and restrained, it has total cover
//    against attacks and other effects outside the behir, and it takes 21 (6d6)
//    acid damage at the start of each of the behir's turns."
//
//   "If the behir takes 30 damage or more on a single turn from the swallowed
//    creature, the behir must succeed on a DC 14 Constitution saving throw at
//    the end of that turn or regurgitate the creature, which falls prone in a
//    space within 10 ft. of the behir."

export interface SwallowRule {
  /** damage at the start of each of the SWALLOWER's turns */
  dice: string;
  dmgType: string;
  /** conditions the swallowed creature has while it lasts */
  conditions: string[];
  /** attacks and effects from outside cannot reach it */
  totalCover: boolean;
  /** damage in one turn from inside that forces the check, and the check itself */
  threshold: number | null;
  saveDc: number | null;
  saveAbility: "str" | "dex" | "con" | "int" | "wis" | "cha" | null;
  /** how many it can hold at once, when the statblock says */
  capacity: number | null;
  /** escaping the corpse when the swallower dies */
  escapeFt: number | null;
}

const ABILITY: Record<string, SwallowRule["saveAbility"]> = { strength: "str", dexterity: "dex", constitution: "con", intelligence: "int", wisdom: "wis", charisma: "cha" };
const WORD: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 };
/** the conditions an SRD swallow actually imposes; anything else is prose */
const CONDITION_WORDS = ["blinded", "restrained", "deafened", "incapacitated", "paralyzed", "poisoned", "stunned"];

const flatten = (v: string) => String(v || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();

/**
 * The swallow mechanics stated by a statblock action, or null when the text is
 * not a swallow.
 *
 * Deliberately strict: BOTH the swallow and the per-turn damage clause must be
 * present. A bite that merely grapples, an engulf, or a swallow-shaped sentence
 * with no damage on record is not enough — a half-read rule that starts dealing
 * dice at the top of a turn is worse than no automation at all.
 */
export function swallowRuleFrom(text: string): SwallowRule | null {
  const t = flatten(text);
  if (!/swallow/i.test(t)) return null;

  // "it takes 21 (6d6) acid damage at the start of each of the behir's turns"
  const dmg = /takes?\s+(?:\d+\s*)?\(?(\d+d\d+(?:\s*\+\s*\d+)?)\)?\s+(\w+)\s+damage\s+at\s+the\s+start\s+of\s+each/i.exec(t);
  if (!dmg) return null;

  const conditions = CONDITION_WORDS
    .filter((c) => new RegExp(`(?:is|are)\\b[^.]*\\b${c}\\b`, "i").test(t))
    .map((c) => c[0].toUpperCase() + c.slice(1));

  const thr = /takes?\s+(\d+)\s+damage or more on a single turn/i.exec(t);
  const save = /DC\s+(\d+)\s+(strength|dexterity|constitution|intelligence|wisdom|charisma)\s+saving throw/i.exec(t);
  const cap = /can have only (\w+) (?:creature|target)s?\s+swallowed/i.exec(t);
  const esc = /using\s+(\d+)\s*(?:ft\.?|feet)\s+of movement/i.exec(t);

  const capN = cap ? (/^\d+$/.test(cap[1]) ? Number(cap[1]) : WORD[cap[1].toLowerCase()]) : null;

  return {
    dice: dmg[1].replace(/\s+/g, ""),
    dmgType: dmg[2].toLowerCase(),
    conditions,
    totalCover: /total cover against attacks/i.test(t),
    threshold: thr ? Number(thr[1]) : null,
    saveDc: save ? Number(save[1]) : null,
    saveAbility: save ? ABILITY[save[2].toLowerCase()] ?? null : null,
    capacity: Number.isFinite(capN as number) ? (capN as number) : null,
    escapeFt: esc ? Number(esc[1]) : null,
  };
}

/** Find the swallow action among a statblock's actions, if it has one. */
export function findSwallowAction(actions: { name: string; text: string }[]): { name: string; rule: SwallowRule } | null {
  for (const a of actions || []) {
    const rule = swallowRuleFrom(`${a.name}. ${a.text}`);
    if (rule) return { name: a.name, rule };
  }
  return null;
}

/**
 * Has enough damage come from inside this turn to force the regurgitate check?
 * Null threshold means the statblock sets none, and no amount of damage forces it.
 */
export const regurgitateDue = (rule: SwallowRule, damageFromInsideThisTurn: number): boolean =>
  rule.threshold !== null && damageFromInsideThisTurn >= rule.threshold;


/** A line the DM can read, for the activity log and the attachment row. */
export function describeSwallow(rule: SwallowRule): string {
  const bits = [`${rule.dice} ${rule.dmgType} at the start of its turns`];
  if (rule.conditions.length) bits.push(rule.conditions.join(" + ").toLowerCase());
  if (rule.totalCover) bits.push("total cover from outside");
  if (rule.threshold !== null && rule.saveDc !== null) bits.push(`${rule.threshold} from inside in one turn: DC ${rule.saveDc} ${String(rule.saveAbility).toUpperCase()} or regurgitate`);
  if (rule.capacity !== null) bits.push(`holds ${rule.capacity}`);
  return bits.join(" · ");
}
